import { StorageProvider } from './types';
import * as Comlink from 'comlink';

/**
 * Interface for the secure worker exposed methods
 */
interface SecureWorkerAPI {
  get(key: string): Promise<string | null>;
  put(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  has(key: string): Promise<boolean>;
}

/**
 * SecureWorker provider implementation using Web Workers and Comlink
 * for storing sensitive information like auth tokens
 */
export class SecureWorkerProvider implements StorageProvider {
  private worker: Worker;
  private api: Comlink.Remote<SecureWorkerAPI>;
  private initialized: Promise<void>;

  constructor(workerUrl: string = '/secure-worker.js') {
    this.worker = new Worker(workerUrl);
    this.api = Comlink.wrap<SecureWorkerAPI>(this.worker);
    
    // Initialize the worker
    this.initialized = new Promise<void>((resolve, reject) => {
      // Wait a short time to ensure the worker is ready
      setTimeout(() => resolve(), 100);
    });
  }

  /**
   * Get a value from the secure worker
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      await this.initialized;
      const value = await this.api.get(key);
      return value ? JSON.parse(value) as T : null;
    } catch (error) {
      console.error('Error retrieving from secure worker:', error);
      return null;
    }
  }

  /**
   * Store a value in the secure worker
   * @param key The key to store the value under
   * @param value The value to store
   */
  async put<T>(key: string, value: T): Promise<void> {
    try {
      await this.initialized;
      await this.api.put(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error storing in secure worker:', error);
      throw error;
    }
  }

  /**
   * Remove a value from the secure worker
   * @param key The key to remove
   */
  async remove(key: string): Promise<void> {
    try {
      await this.initialized;
      await this.api.remove(key);
    } catch (error) {
      console.error('Error removing from secure worker:', error);
      throw error;
    }
  }

  /**
   * Check if a key exists in the secure worker
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   */
  async has(key: string): Promise<boolean> {
    try {
      await this.initialized;
      return await this.api.has(key);
    } catch (error) {
      console.error('Error checking key in secure worker:', error);
      return false;
    }
  }
  
  /**
   * Get the storage type name
   */
  getStorageType(): string {
    return 'secureWorker';
  }

  /**
   * Terminate the worker when no longer needed
   */
  terminate(): void {
    this.worker.terminate();
  }
}